/* eslint-disable jsx-a11y/interactive-supports-focus */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React from "react";
import { useTheme } from "react-jss";
import classnames from "classnames";
import { FontAwesomeIcon } from "../../shared/FontAwesome";
import useStyles from "./StarRatingRenderer.styles";
import { RendererProps } from "../BaseRenderer";
import { translate } from "../../../models/utilities/translationUtilities";
import useQuestion from "../../../hooks/useQuestion";
import { useKeyboardNavigation } from "../../../hooks/useKeyboardNavigation";
import { StarRatingNavigation } from "../../controls/KeyboardNavigation/StarRatingNavigation";
import useTextDirection from "../../../hooks/useTextDirection";
import { QuestionType } from "../../../models/enums/QuestionType";
import { shouldUseModernTheme } from "../../../theme/navigationButtonThemeColors";
import type { Theme } from "../../../types/Theme";

interface Props extends RendererProps {
    index: number;
    setValue(value: number, reset?: boolean): void;
    value: number;
    elementId: string;
    split: number;
    starWidth?: number;
    starHeight?: number;
    isDisabled?: boolean;
    rerenderParentCallback(input: number): void;
    hoveredStarValue: number;
    hasError?: boolean;
}

export default function StarRenderer(props: Props) {
    const {
        index,
        setValue,
        value,
        elementId,
        split = 1,
        starWidth = 24,
        starHeight = 24,
        isDisabled = false,
        variable,
        rerenderParentCallback,
        hoveredStarValue,
        hasError = false,
        gridRendering,
        gridRenderedRowIndex,
        theme: surveyThemeForI18n,
        language,
    } = props;

    const question = useQuestion();
    const theme = useTheme();
    const isRtl = useTextDirection() === "rtl";
    const spaceKeyCallBack = () => {
        if (!isDisabled) {
            setValue(starValue);
        }
    };
    const starUniqueId = `${elementId}_INPUT_${index}`;
    const { elementReference, onKeyDown } = useKeyboardNavigation(
        starUniqueId,
        variable,
        question,
        StarRatingNavigation,
        spaceKeyCallBack,
    );

    const starValue = (index + 1) / split;
    const splitWidth = starWidth / split;
    const isIconHighlighted =
        hoveredStarValue > 0 ? starValue <= hoveredStarValue : starValue <= value;
    const modern = shouldUseModernTheme(theme as Theme);
    const classes = useStyles({
        theme,
        starWidth,
        starHeight,
        splitWidth,
        filled: isIconHighlighted,
        hasError,
    });

    // NVDA already says "n of m" in the radiogroup; avoid "1 star"/"2 stars" in the name (repeats "star").
    // aria-roledescription keeps "star" in the UX instead of a generic "radio" label.
    const inHybridGridStarColumn =
        gridRendering === true &&
        question?.parent?.type === QuestionType.HybridGrid &&
        typeof gridRenderedRowIndex === "number";

    const starRoleDescription = inHybridGridStarColumn
        ? surveyThemeForI18n && language
            ? translate(
                  surveyThemeForI18n,
                  "StarRatingStarRoleDescription",
                  language,
                  "star",
              ) || "star"
            : "star"
        : undefined;

    const firstStarInSequentialTabOrder =
        !inHybridGridStarColumn || gridRenderedRowIndex === 0;
    const tabIndex = isDisabled
        ? -1
        : index !== 0
          ? -1
          : gridRendering
            ? 0
            : firstStarInSequentialTabOrder
              ? 0
              : -1;

    return (
        <div
            onKeyDown={onKeyDown}
            ref={elementReference}
            onClick={isDisabled ? undefined : () => setValue(starValue)}
            onMouseOver={isDisabled ? undefined : () => rerenderParentCallback(starValue)}
            className={`${classnames(
                classes.starRatingStar,
                modern && classes.starRatingStarModernFocus,
            )} ${isDisabled ? classes.disabled : ""}`}
            aria-label={String(starValue)}
            {...(starRoleDescription !== undefined
                ? { "aria-roledescription": starRoleDescription }
                : {})}
            // eslint-disable-next-line jsx-a11y/role-has-required-aria-props
            role="radio"
            tabIndex={tabIndex}
            aria-checked={isIconHighlighted}
        >
            <FontAwesomeIcon
                {...(inHybridGridStarColumn ? { "aria-hidden": true } : {})}
                style={{
                    position: "absolute",
                    width: starWidth,
                    height: starHeight,
                    // Each half-star is an overflow-hidden window over a full-width glyph: the "left"
                    // half anchors the glyph left, the "right" half anchors it right. The parent
                    // `.star-rating` is a flex row, so in RTL the two half windows swap visual order —
                    // anchor to the opposite physical edge so the halves still reassemble into an
                    // intact star (DEV-98352).
                    left: (index % split === 0) !== isRtl ? 0 : undefined,
                    right: (index % split === 1) !== isRtl ? 0 : undefined,
                }}
                icon={[isIconHighlighted ? "fas" : "far", "star"]}
            />
        </div>
    );
}
